import { createRouter, createWebHistory } from 'vue-router';
import { storageReadyPromise } from '@/utilities/resolvers/storageReadyResolver';
import { store } from '@/appContext';
import eventBus from '@/utilities/EventBus';
import routes from '../constants/routes';

// https://router.vuejs.org/guide/advanced/scroll-behavior.html#async-scrolling
export const scrollBehavior = (to, from, savedPosition) => {
  if (to.path === '/market-report') {
    // Don't scroll to top when only query params change
    // See: https://github.com/vuejs/vue-router/issues/2072
    if (from && Object.keys(to.query).length) {
      if (to.fullPath.split('?')[0] === from.fullPath.split('?')[0]) {
        return null;
      }
    }
  }

  if (
    ['Search', 'Marketplace'].includes(from.name)
    && ['Search', 'Marketplace'].includes(to.name)
  ) {
    return savedPosition;
  }

  const resetCoordinates = { left: 0, top: 0 };
  return savedPosition || resetCoordinates;
};

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior,
});

const setPageTitle = (to) => {
  const titlePrefix = 'ACV Auctions';

  // if the route has a title specified, let's use it
  if (to.meta.title) {
    document.title = `${titlePrefix} | ${to.meta.title}`;
  } else {
    document.title = titlePrefix;
  }
};

const routeUser = (to, from, next) => {
  const { isAuthorized } = store.getters;
  const { canSell } = store.getters;

  const { name, fullPath } = to;

  if (!to.meta.allowUnauthorized && !isAuthorized) {
    // keep route last visited so login can redirect back to it if unauthorized
    if (!['login', 'logout'].includes(name)) {
      store.dispatch('setGoToAfterLogin', fullPath);
    }

    // navigate to logout in order to route to login
    next({ path: '/logout', replace: true });
    return;
  }

  // clear last route visited after user is authorized
  if (!['login', 'logout'].includes(name)) {
    store.dispatch('clearGoToAfterLogin');
  }

  if (to.meta.sellerOnly && !canSell) {
    next('/auctions/active');
    return;
  }

  // if we don't match a route in vue, send to nginx/web-php via browser
  if (to.matched.length) {
    setPageTitle(to);
    next();
  } else {
    window.location.replace(to.fullPath);
  }
};

// Set when onNeedRefresh fires (new SW has finished precaching all new chunks locally).
// Cleared after the reload is triggered so it only fires once per update.
let swUpdateReady = false;
eventBus.$on('acv::app-update-ready', () => {
  swUpdateReady = true;
});

export const beforeEachGuard = async (to, from, next) => {
  // New SW is waiting with all new chunks precached. Reload on this navigation to
  // the page the user is already leaving towards so there's no in-page state to lose,
  // and the reload will be served instantly from Cache Storage (no network needed)
  if (swUpdateReady && to.name !== from.name) {
    swUpdateReady = false;
    eventBus.$emit('acv::app-trigger-update', to.fullPath);
    next(false);
    return;
  }

  // Remove acv_cf query parameter from URL
  if (to.query?.acv_cf) {
    const { acv_cf: _, ...query } = to.query;

    next({
      path: to.path,
      query,
      hash: to.hash,
      replace: true,
    });
    return;
  }

  const { authorizationIsReady } = store.getters;

  if (!authorizationIsReady) {
    await storageReadyPromise; // Wait for storage resolve before routing triggered from userStorage

    await store.dispatch('updateReady', true);
    routeUser(to, from, next);
  } else {
    routeUser(to, from, next);
  }
};

export const afterEachGuard = (to, from) => {
  store.dispatch('updateRoute', { to, from });

  if (
    (from.name === 'auction' || from.name === 'saved')
    && !Object.keys(from.query).includes('action')
  ) {
    store.dispatch('marketplaceAuctions/clearViewingAuctionModel');
    store.dispatch('pandaMfe/clearPandaAuction');
  }
};

router.beforeEach(beforeEachGuard);

router.afterEach(afterEachGuard);

export default router;
